import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
    LayoutDashboard,
    FileText,
    MessageSquare,
    Bot,
    Users,
    Settings,
    LogOut,
    ChevronsLeft,
    ChevronsRight,
    ChevronDown,
    ChevronRight,
    Instagram,
    Music2,
    Facebook
} from 'lucide-react';
import XIcon from './XIcon';
import { getMyPermissions } from '../services/api';

interface SidebarProps {
    isCollapsed: boolean;
    setIsCollapsed: (value: boolean) => void;
}

interface RolePermissions {
    dashboard: boolean;
    comment_list: boolean;
    ai_response: boolean;
    user_comments: boolean;
    all_posts: boolean;
    statistics: boolean;
    users_management: boolean;
    settings: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ isCollapsed, setIsCollapsed }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const userRole = localStorage.getItem('user_role');
    const [isAIResponseExpanded, setIsAIResponseExpanded] = useState(true);
    const [permissions, setPermissions] = useState<RolePermissions>({
        dashboard: true,
        comment_list: true,
        ai_response: true,
        user_comments: true,
        all_posts: true,
        statistics: true,
        users_management: true,
        settings: true
    });

    useEffect(() => {
        // Fetch permissions for current user
        const fetchPermissions = async () => {
            try {
                const perms = await getMyPermissions();
                setPermissions(perms);
            } catch (error) {
                console.error('Failed to fetch permissions:', error);
                // Set default minimal permissions on error
                setPermissions({
                    dashboard: true,
                    comment_list: true,
                    ai_response: false,
                    user_comments: true,
                    all_posts: false,
                    statistics: true,
                    users_management: false,
                    settings: false
                });
            }
        };

        fetchPermissions();
    }, []);

    const toggleSidebar = () => {
        setIsCollapsed(!isCollapsed);
    };

    const handleLogout = () => {
        localStorage.removeItem('access_token');
        localStorage.removeItem('user_role');
        navigate('/login');
    };

    return (
        <div className={`sidebar ${isCollapsed ? 'collapsed' : ''}`}>
            <div className="sidebar-header">
                <button className="collapse-btn" onClick={toggleSidebar}>
                    {isCollapsed ? <ChevronsRight size={20} /> : <ChevronsLeft size={20} />}
                </button>
                <div className="logo-container">
                    <img src="/logo.svg" alt="Social Mind" className="logo-icon" />
                    {!isCollapsed && (
                        <div className="logo-text" style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '2px',
                            marginLeft: '4px'
                        }}>
                            <h2 style={{
                                fontSize: '26px',
                                fontWeight: '400',
                                color: 'var(--text-primary)',
                                margin: '0',
                                fontFamily: 'Outfit, sans-serif',
                                letterSpacing: '-0.02em',
                                lineHeight: '1.2'
                            }}>Social Mind</h2>
                            <span style={{
                                fontSize: '12px',
                                fontWeight: '400',
                                color: 'var(--text-secondary)',
                                fontFamily: 'Outfit, sans-serif',
                                letterSpacing: '0.2px'
                            }}>By Iterate.ai</span>
                        </div>
                    )}
                </div>
            </div>

            <nav className="sidebar-nav">
                {/* Dashboard */}
                {permissions.dashboard && (
                    <NavLink to="/" className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'}>
                        <LayoutDashboard size={20} />
                        {!isCollapsed && <span>Dashboard</span>}
                    </NavLink>
                )}

                {/* Post List */}
                {permissions.all_posts && (
                    <NavLink to="/posts" className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'}>
                        <FileText size={20} />
                        {!isCollapsed && <span>Post List</span>}
                    </NavLink>
                )}

                {/* Comment List */}
                {permissions.comment_list && (
                    <NavLink to="/comments" className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'}>
                        <MessageSquare size={20} />
                        {!isCollapsed && <span>Comment List</span>}
                    </NavLink>
                )}

                {/* AI Response */}
                {permissions.ai_response && (
                    <div className="nav-group">
                        <div
                            className={`nav-item ${location.pathname.includes('/ai-responses') ? 'active' : ''}`}
                            onClick={() => {
                                if (!isCollapsed) {
                                    setIsAIResponseExpanded(!isAIResponseExpanded);
                                } else {
                                    navigate('/ai-responses?platform=INSTAGRAM');
                                }
                            }}
                            style={{ cursor: 'pointer', justifyContent: 'space-between' }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                <Bot size={20} />
                                {!isCollapsed && <span>AI Response</span>}
                                {isCollapsed && (
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px', marginLeft: '8px' }}>
                                        <div
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                navigate('/ai-responses?platform=INSTAGRAM');
                                            }}
                                            style={{
                                                width: '10px',
                                                height: '10px',
                                                borderRadius: '50%',
                                                background: 'linear-gradient(135deg, #E1306C 0%, #C13584 100%)',
                                                cursor: 'pointer',
                                                transition: 'transform 0.2s ease',
                                                border: location.search.includes('INSTAGRAM') ? '2px solid #ffffff' : 'none',
                                                boxShadow: '0 1px 3px rgba(225, 48, 108, 0.4)'
                                            }}
                                            onMouseEnter={(e) => (e.target as HTMLElement).style.transform = 'scale(1.3)'}
                                            onMouseLeave={(e) => (e.target as HTMLElement).style.transform = 'scale(1)'}
                                            title="Instagram AI Responses"
                                        />
                                        <div
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                navigate('/ai-responses?platform=TIKTOK');
                                            }}
                                            style={{
                                                width: '10px',
                                                height: '10px',
                                                borderRadius: '50%',
                                                backgroundColor: '#000000',
                                                cursor: 'pointer',
                                                transition: 'transform 0.2s ease',
                                                border: location.search.includes('TIKTOK') ? '2px solid #ffffff' : 'none',
                                                boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                                            }}
                                            onMouseEnter={(e) => (e.target as HTMLElement).style.transform = 'scale(1.3)'}
                                            onMouseLeave={(e) => (e.target as HTMLElement).style.transform = 'scale(1)'}
                                            title="TikTok AI Responses"
                                        />
                                        <div
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                navigate('/ai-responses?platform=TWITTER');
                                            }}
                                            style={{
                                                width: '10px',
                                                height: '10px',
                                                borderRadius: '50%',
                                                backgroundColor: '#000000',
                                                cursor: 'pointer',
                                                transition: 'transform 0.2s ease',
                                                border: location.search.includes('TWITTER') ? '2px solid #ffffff' : 'none',
                                                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.4)'
                                            }}
                                            onMouseEnter={(e) => (e.target as HTMLElement).style.transform = 'scale(1.3)'}
                                            onMouseLeave={(e) => (e.target as HTMLElement).style.transform = 'scale(1)'}
                                            title="X AI Responses"
                                        />
                                        <div
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                navigate('/ai-responses?platform=FACEBOOK');
                                            }}
                                            style={{
                                                width: '10px',
                                                height: '10px',
                                                borderRadius: '50%',
                                                backgroundColor: '#1877F2',
                                                cursor: 'pointer',
                                                transition: 'transform 0.2s ease',
                                                border: location.search.includes('FACEBOOK') ? '2px solid #ffffff' : 'none',
                                                boxShadow: '0 1px 3px rgba(24, 119, 242, 0.4)'
                                            }}
                                            onMouseEnter={(e) => (e.target as HTMLElement).style.transform = 'scale(1.3)'}
                                            onMouseLeave={(e) => (e.target as HTMLElement).style.transform = 'scale(1)'}
                                            title="Facebook AI Responses"
                                        />
                                    </div>
                                )}
                            </div>
                            {!isCollapsed && (isAIResponseExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />)}
                        </div>

                        {!isCollapsed && isAIResponseExpanded && (
                            <div className="submenu" style={{ paddingLeft: '2.5rem', display: 'flex', flexDirection: 'column', gap: '0.25rem', marginTop: '0.25rem' }}>
                                <NavLink to="/ai-responses?platform=INSTAGRAM" className={({ isActive }) => location.search.includes('INSTAGRAM') ? 'nav-item active' : 'nav-item'} style={{ padding: '0.5rem 0.75rem', fontSize: '0.9rem' }}>
                                    <Instagram size={16} />
                                    <span>Instagram</span>
                                </NavLink>
                                <NavLink to="/ai-responses?platform=TIKTOK" className={({ isActive }) => location.search.includes('TIKTOK') ? 'nav-item active' : 'nav-item'} style={{ padding: '0.5rem 0.75rem', fontSize: '0.9rem' }}>
                                    <Music2 size={16} />
                                    <span>TikTok</span>
                                </NavLink>
                                <NavLink to="/ai-responses?platform=TWITTER" className={({ isActive }) => location.search.includes('TWITTER') ? 'nav-item active' : 'nav-item'} style={{ padding: '0.5rem 0.75rem', fontSize: '0.9rem' }}>
                                    <XIcon size={16} />
                                    <span>X</span>
                                </NavLink>
                                <NavLink to="/ai-responses?platform=FACEBOOK" className={({ isActive }) => location.search.includes('FACEBOOK') ? 'nav-item active' : 'nav-item'} style={{ padding: '0.5rem 0.75rem', fontSize: '0.9rem' }}>
                                    <Facebook size={16} />
                                    <span>Facebook</span>
                                </NavLink>
                            </div>
                        )}
                    </div>
                )}

                {/* Users */}
                {permissions.users_management && (
                    <NavLink to="/users" className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'}>
                        <Users size={20} />
                        {!isCollapsed && <span>Users</span>}
                    </NavLink>
                )}

                {/* Settings */}
                {permissions.settings && (
                    <NavLink to="/settings" className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'}>
                        <Settings size={20} />
                        {!isCollapsed && <span>Settings</span>}
                    </NavLink>
                )}
                <button className="nav-item logout-btn" onClick={handleLogout}>
                    <LogOut size={20} />
                    {!isCollapsed && <span>Logout</span>}
                </button>
            </nav>
        </div>
    );
};

export default Sidebar;
